import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TableChartIcon from '@mui/icons-material/TableChart';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useParams } from 'react-router-dom';
import {
  subscribeToReservations,
  subscribeToReservationPackagesAdmin,
  updateReservationStatus,
  createReservationAdmin,
  deleteReservation,
  createReservationPackageAdmin,
  updateReservationPackageAdmin,
  deleteReservationPackageAdmin,
  Reservation,
  ReservationPackage,
  ReservationStatus,
} from '../services/reservationsService';

function formatPrice(value: number) {
  return value.toLocaleString('es-CO', { minimumFractionDigits: 0 });
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function formatDateStr(year: number, monthZeroBased: number, day: number) {
  const mm = String(monthZeroBased + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

export default function ReservationsPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [tab, setTab] = useState<number>(0);

  // Modo de vista: 'calendar' o 'table'
  const [viewMode, setViewMode] = useState<'calendar' | 'table'>('calendar');

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [packages, setPackages] = useState<ReservationPackage[]>([]);

  // Mes seleccionado para el calendario
  const [currentMonthDate, setCurrentMonthDate] = useState<Date>(new Date());

  // Filtros de Reservas
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('todos');

  // Modal Reserva Manual
  const [openResModal, setOpenResModal] = useState(false);
  const [resForm, setResForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    guestCount: 2,
    date: new Date().toISOString().split('T')[0],
    time: '19:00',
    packageTitle: 'Reserva Estándar',
    packagePrice: 0,
    notes: '',
  });

  // Modal Paquete
  const [openPkgModal, setOpenPkgModal] = useState(false);
  const [editingPkgId, setEditingPkgId] = useState<string | null>(null);
  const [pkgForm, setPkgForm] = useState({
    title: '',
    description: '',
    price: 0,
    maxGuests: 4,
    featuresText: '',
    active: true,
  });

  // Modal de Detalle de Día / Reservas del Día en Calendario
  const [openDayModal, setOpenDayModal] = useState(false);
  const [dayModalDate, setDayModalDate] = useState<string>('');

  // Modal de Detalle / Edición Rápida de Reserva
  const [selectedResForDetail, setSelectedResForDetail] = useState<Reservation | null>(null);

  // Cargar datos
  useEffect(() => {
    if (!restaurantId) return;
    const unsub1 = subscribeToReservations(restaurantId, (data) => setReservations(data));
    const unsub2 = subscribeToReservationPackagesAdmin(restaurantId, (data) => setPackages(data));
    return () => {
      unsub1();
      unsub2();
    };
  }, [restaurantId]);

  // Filtrado de Reservas para Tabla / Lista
  const filteredReservations = useMemo(() => {
    return reservations.filter((r) => {
      if (selectedDate && r.date !== selectedDate) return false;
      if (statusFilter !== 'todos' && r.state !== statusFilter) return false;
      return true;
    });
  }, [reservations, selectedDate, statusFilter]);

  // Mapa de reservas por fecha para el Calendario: { "YYYY-MM-DD": Reservation[] }
  const reservationsByDate = useMemo(() => {
    const map: Record<string, Reservation[]> = {};
    reservations.forEach((res) => {
      if (statusFilter !== 'todos' && res.state !== statusFilter) return;
      if (!map[res.date]) {
        map[res.date] = [];
      }
      map[res.date].push(res);
    });
    // Ordenar reservas de cada día por hora
    Object.keys(map).forEach((d) => {
      map[d].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    });
    return map;
  }, [reservations, statusFilter]);

  // Construcción del Grid Mensual del Calendario
  const calendarGridDays = useMemo(() => {
    const year = currentMonthDate.getFullYear();
    const month = currentMonthDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Ajustar para que la semana empiece en Lunes (0) a Domingo (6)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;

    const daysInMonth = lastDay.getDate();
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days: Array<{
      dateStr: string;
      dayNum: number;
      isCurrentMonth: boolean;
      isToday: boolean;
    }> = [];

    const todayStr = new Date().toISOString().split('T')[0];

    // Días del mes anterior (padding)
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const prevDateStr = formatDateStr(month === 0 ? year - 1 : year, month === 0 ? 11 : month - 1, d);
      days.push({
        dateStr: prevDateStr,
        dayNum: d,
        isCurrentMonth: false,
        isToday: prevDateStr === todayStr,
      });
    }

    // Días del mes actual
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = formatDateStr(year, month, d);
      days.push({
        dateStr,
        dayNum: d,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
      });
    }

    // Días del siguiente mes (padding para completar filas de 7 días)
    const totalCells = days.length;
    const remaining = (7 - (totalCells % 7)) % 7;
    for (let d = 1; d <= remaining; d++) {
      const nextDateStr = formatDateStr(month === 11 ? year + 1 : year, month === 11 ? 0 : month + 1, d);
      days.push({
        dateStr: nextDateStr,
        dayNum: d,
        isCurrentMonth: false,
        isToday: nextDateStr === todayStr,
      });
    }

    return days;
  }, [currentMonthDate]);

  // Métricas rápidas
  const todayStr = new Date().toISOString().split('T')[0];
  const todayCount = reservations.filter((r) => r.date === todayStr).length;
  const pendingCount = reservations.filter((r) => r.state === 'pendiente').length;
  const confirmedCount = reservations.filter((r) => r.state === 'confirmada').length;

  // Navegación de Mes
  const handlePrevMonth = () => {
    setCurrentMonthDate(new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentMonthDate(new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() + 1, 1));
  };
  const handleToday = () => {
    setCurrentMonthDate(new Date());
    setSelectedDate(todayStr);
  };

  // Abrir modal de creación para un día específico
  const handleCreateForDate = (dateStr: string) => {
    setResForm((prev) => ({
      ...prev,
      date: dateStr,
    }));
    setOpenResModal(true);
  };

  // Abrir modal con reservaciones del día
  const handleDayClick = (dateStr: string) => {
    setDayModalDate(dateStr);
    setOpenDayModal(true);
  };

  // Acciones de Reservas
  const handleStatusChange = async (resId: string, status: ReservationStatus) => {
    if (!restaurantId) return;
    try {
      await updateReservationStatus(restaurantId, resId, status);
      if (selectedResForDetail && selectedResForDetail.id === resId) {
        setSelectedResForDetail((prev) => (prev ? { ...prev, state: status } : null));
      }
    } catch (err) {
      console.error(err);
      alert('Error al actualizar estado');
    }
  };

  const handleDeleteRes = async (resId: string) => {
    if (!restaurantId) return;
    if (window.confirm('¿Seguro que deseas eliminar esta reserva?')) {
      await deleteReservation(restaurantId, resId);
      if (selectedResForDetail?.id === resId) {
        setSelectedResForDetail(null);
      }
    }
  };

  const handleCreateReservation = async () => {
    if (!restaurantId) return;
    if (!resForm.customerName || !resForm.customerPhone || !resForm.date || !resForm.time) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const code = `RES-${Math.floor(1000 + Math.random() * 9000)}`;
    await createReservationAdmin(restaurantId, {
      reservationCode: code,
      customerName: resForm.customerName,
      customerPhone: resForm.customerPhone,
      customerEmail: resForm.customerEmail,
      guestCount: resForm.guestCount,
      date: resForm.date,
      time: resForm.time,
      packageTitle: resForm.packageTitle,
      packagePrice: resForm.packagePrice,
      notes: resForm.notes,
      state: 'confirmada',
    });
    setOpenResModal(false);
    setResForm({
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      guestCount: 2,
      date: new Date().toISOString().split('T')[0],
      time: '19:00',
      packageTitle: 'Reserva Estándar',
      packagePrice: 0,
      notes: '',
    });
  };

  // Acciones de Paquetes
  const handleOpenNewPkg = () => {
    setEditingPkgId(null);
    setPkgForm({
      title: '',
      description: '',
      price: 0,
      maxGuests: 4,
      featuresText: '',
      active: true,
    });
    setOpenPkgModal(true);
  };

  const handleEditPkg = (pkg: ReservationPackage) => {
    setEditingPkgId(pkg.id);
    setPkgForm({
      title: pkg.title,
      description: pkg.description || '',
      price: pkg.price || 0,
      maxGuests: pkg.maxGuests || 4,
      featuresText: (pkg.features || []).join('\n'),
      active: pkg.active !== false,
    });
    setOpenPkgModal(true);
  };

  const handleSavePkg = async () => {
    if (!restaurantId) return;
    if (!pkgForm.title) {
      alert('El título es requerido');
      return;
    }
    const features = pkgForm.featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);

    const payload = {
      title: pkgForm.title,
      description: pkgForm.description,
      price: Number(pkgForm.price),
      maxGuests: Number(pkgForm.maxGuests),
      features,
      active: pkgForm.active,
    };

    if (editingPkgId) {
      await updateReservationPackageAdmin(restaurantId, editingPkgId, payload);
    } else {
      await createReservationPackageAdmin(restaurantId, payload);
    }
    setOpenPkgModal(false);
  };

  const handleDeletePkg = async (pkgId: string) => {
    if (!restaurantId) return;
    if (window.confirm('¿Seguro que deseas eliminar este paquete de reserva?')) {
      await deleteReservationPackageAdmin(restaurantId, pkgId);
    }
  };

  const handleTogglePkgActive = async (pkg: ReservationPackage) => {
    if (!restaurantId) return;
    await updateReservationPackageAdmin(restaurantId, pkg.id, { active: !pkg.active });
  };

  // Helper para renderizar chip de estado
  const renderStatusChip = (state: ReservationStatus) => {
    switch (state) {
      case 'pendiente':
        return <Chip label="⏳ Pendiente" size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 800 }} />;
      case 'confirmada':
        return <Chip label="✅ Confirmada" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 800 }} />;
      case 'completada':
        return <Chip label="🎉 Completada" size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 800 }} />;
      case 'cancelada':
        return <Chip label="❌ Cancelada" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 800 }} />;
      default:
        return null;
    }
  };

  // Color de borde de mini chip en celda del calendario
  const getStatusBgColor = (state: ReservationStatus) => {
    switch (state) {
      case 'pendiente':
        return { bg: '#fffbebeb', color: '#92400e', border: '#fde68a' };
      case 'confirmada':
        return { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' };
      case 'completada':
        return { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' };
      case 'cancelada':
        return { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' };
      default:
        return { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Encabezado */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900} color="#0f172a" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <EventAvailableIcon fontSize="large" sx={{ color: '#001a64' }} />
            Módulo de Reservas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión de calendario de reservaciones y paquetes para Thomas Pizza
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenResModal(true)}
          sx={{
            bgcolor: '#001a64',
            color: '#fff',
            borderRadius: 3,
            fontWeight: 800,
            px: 3,
            py: 1.2,
            width: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
            '&:hover': { bgcolor: '#001242' },
          }}
        >
          Crear Reserva Manual
        </Button>
      </Stack>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 3, elevation: 0, border: '1px solid #e2e8f0' }}>
        <Tabs
          value={tab}
          onChange={(_, val) => setTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', fontSize: { xs: '0.875rem', sm: '1rem' }, py: 2 },
            '& .Mui-selected': { color: '#001a64' },
          }}
        >
          <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Calendario & Reservas" />
          <Tab icon={<CardGiftcardIcon />} iconPosition="start" label={`Paquetes de Reserva (${packages.length})`} />
        </Tabs>
      </Paper>

      {/* ── PESTAÑA 1: CALENDARIO & RESERVAS ── */}
      {tab === 0 && (
        <Box>
          {/* Tarjetas KPI */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8' }}>
                    <CalendarMonthIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>RESERVAS HOY</Typography>
                    <Typography variant="h5" fontWeight={900} color="#0f172a">{todayCount}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b45309' }}>
                    <HourglassEmptyIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>PENDIENTES</Typography>
                    <Typography variant="h5" fontWeight={900} color="#b45309">{pendingCount}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#15803d' }}>
                    <CheckCircleIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>CONFIRMADAS</Typography>
                    <Typography variant="h5" fontWeight={900} color="#15803d">{confirmedCount}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Barra de Controles: Selector de Vista, Navegación de Mes y Filtros */}
          <Paper sx={{ p: 2, borderRadius: 3, mb: 3, border: '1px solid #e2e8f0', elevation: 0 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
              
              {/* Conmutador Vista Calendario / Vista Tabla */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, nextView) => nextView && setViewMode(nextView)}
                size="small"
                sx={{
                  bgcolor: '#f1f5f9',
                  p: 0.5,
                  borderRadius: 2.5,
                  '& .MuiToggleButton-root': {
                    border: 0,
                    borderRadius: 2,
                    fontWeight: 800,
                    px: 2,
                    py: 0.8,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      bgcolor: '#001a64',
                      color: '#fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: '#001242' },
                    },
                  },
                }}
              >
                <ToggleButton value="calendar">
                  <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
                  Vista Calendario
                </ToggleButton>
                <ToggleButton value="table">
                  <TableChartIcon sx={{ mr: 1, fontSize: 18 }} />
                  Vista Lista / Tabla
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Filtro por estado y fecha (si aplica) */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 180, width: { xs: '100%', sm: 'auto' } }}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Estado"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="todos">Todos los Estados</MenuItem>
                    <MenuItem value="pendiente">⏳ Pendientes</MenuItem>
                    <MenuItem value="confirmada">✅ Confirmadas</MenuItem>
                    <MenuItem value="completada">🎉 Completadas</MenuItem>
                    <MenuItem value="cancelada">❌ Canceladas</MenuItem>
                  </Select>
                </FormControl>

                {viewMode === 'table' && (
                  <TextField
                    label="Filtrar Fecha"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  />
                )}

                {selectedDate && viewMode === 'table' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedDate('')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Limpiar Fecha
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          {/* ── MODO 1: CALENDARIO MENSUAL ── */}
          {viewMode === 'calendar' && (
            <Paper sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3, border: '1px solid #e2e8f0', elevation: 0 }}>
              {/* Header Navegación del Calendario */}
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h5" fontWeight={900} color="#0f172a" sx={{ textTransform: 'capitalize' }}>
                    {MONTH_NAMES[currentMonthDate.getMonth()]} {currentMonthDate.getFullYear()}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<TodayIcon />}
                    onClick={handleToday}
                    sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
                  >
                    Hoy
                  </Button>
                  <IconButton onClick={handlePrevMonth} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton onClick={handleNextMonth} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Leyenda de colores */}
              <Stack direction="row" flexWrap="wrap" spacing={2} sx={{ mb: 2, px: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#b45309' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Pendiente</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#15803d' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Confirmada</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1d4ed8' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Completada</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#dc2626' }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Cancelada</Typography>
                </Stack>
              </Stack>

              {/* Grid de Días de la semana */}
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {WEEKDAYS.map((dayName) => (
                  <Grid size={{ xs: 12 / 7 }} key={dayName}>
                    <Box sx={{ p: 1, textAlign: 'center', bgcolor: '#f1f5f9', borderRadius: 1.5 }}>
                      <Typography variant="caption" fontWeight={800} color="#334155">
                        {dayName}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Rejilla del Mes */}
              <Grid container spacing={1}>
                {calendarGridDays.map((cell, idx) => {
                  const dayReservations = reservationsByDate[cell.dateStr] || [];
                  const maxVisible = isMobile ? 1 : isTablet ? 2 : 3;
                  const visibleReservations = dayReservations.slice(0, maxVisible);
                  const overflowCount = dayReservations.length - visibleReservations.length;

                  return (
                    <Grid size={{ xs: 12 / 7 }} key={`${cell.dateStr}-${idx}`}>
                      <Box
                        onClick={() => handleDayClick(cell.dateStr)}
                        sx={{
                          minHeight: { xs: 80, sm: 110, md: 130 },
                          p: { xs: 0.5, sm: 1 },
                          borderRadius: 2,
                          border: cell.isToday
                            ? '2px solid #001a64'
                            : cell.isCurrentMonth
                            ? '1px solid #e2e8f0'
                            : '1px stroke #f1f5f9',
                          bgcolor: cell.isToday
                            ? '#f0f4ff'
                            : cell.isCurrentMonth
                            ? '#ffffff'
                            : '#f8fafc',
                          opacity: cell.isCurrentMonth ? 1 : 0.5,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          transition: 'all 0.15s ease-in-out',
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                            borderColor: '#cbd5e1',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          },
                        }}
                      >
                        {/* Cabecera del día */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={cell.isToday ? 900 : 700}
                            sx={{
                              color: cell.isToday ? '#001a64' : cell.isCurrentMonth ? '#0f172a' : '#94a3b8',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              bgcolor: cell.isToday ? '#dbeafe' : 'transparent',
                              px: cell.isToday ? 0.8 : 0,
                              py: cell.isToday ? 0.2 : 0,
                              borderRadius: 1,
                            }}
                          >
                            {cell.dayNum}
                          </Typography>

                          {dayReservations.length > 0 && (
                            <Chip
                              label={dayReservations.length}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 900,
                                bgcolor: '#001a64',
                                color: '#fff',
                              }}
                            />
                          )}
                        </Stack>

                        {/* Mini lista de reservas dentro de la celda */}
                        <Stack spacing={0.5} sx={{ flexGrow: 1, overflow: 'hidden' }}>
                          {visibleReservations.map((res) => {
                            const style = getStatusBgColor(res.state);
                            return (
                              <Box
                                key={res.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedResForDetail(res);
                                }}
                                sx={{
                                  p: 0.5,
                                  borderRadius: 1,
                                  bgcolor: style.bg,
                                  color: style.color,
                                  border: `1px solid ${style.border}`,
                                  fontSize: { xs: '0.65rem', sm: '0.72rem' },
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  '&:hover': {
                                    filter: 'brightness(0.95)',
                                  },
                                }}
                              >
                                {res.time} - {res.customerName} ({res.guestCount}p)
                              </Box>
                            );
                          })}

                          {overflowCount > 0 && (
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              color="primary"
                              sx={{ fontSize: '0.65rem', textDecoration: 'underline', pt: 0.2 }}
                            >
                              + {overflowCount} más...
                            </Typography>
                          )}
                        </Stack>

                        {/* Botón rápido crear al pasar cursor */}
                        <Box sx={{ pt: 0.5, textAlign: 'right' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateForDate(cell.dateStr);
                            }}
                            sx={{ p: 0.2, color: '#64748b', opacity: 0.6, '&:hover': { opacity: 1, color: '#001a64' } }}
                            title="Agregar reserva en esta fecha"
                          >
                            <AddIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}

          {/* ── MODO 2: LISTA / TABLA ── */}
          {viewMode === 'table' && (
            <Box>
              {/* VISTA MOBILE vs DESKTOP DE RESERVAS */}
              {isMobile ? (
                <Stack spacing={2}>
                  {filteredReservations.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0', elevation: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron reservas con los filtros aplicados.
                      </Typography>
                    </Paper>
                  ) : (
                    filteredReservations.map((res) => {
                      const cleanPhone = res.customerPhone ? res.customerPhone.replace(/\D/g, '') : '';
                      const waUrl = cleanPhone ? `https://wa.me/${cleanPhone}` : null;

                      return (
                        <Card key={res.id} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            {/* Código & Estado */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                              <Typography variant="subtitle1" fontWeight={900} color="#001a64">
                                #{res.reservationCode}
                              </Typography>
                              {renderStatusChip(res.state)}
                            </Stack>

                            {/* Datos del Cliente */}
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" fontWeight={800} color="#0f172a">{res.customerName}</Typography>
                              <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>{res.customerPhone}</Typography>
                                {waUrl && (
                                  <Button
                                    size="small"
                                    component="a"
                                    href={waUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                                    sx={{ color: '#25D366', textTransform: 'none', py: 0, px: 1, minWidth: 0, fontWeight: 700, fontSize: '0.75rem' }}
                                  >
                                    WhatsApp
                                  </Button>
                                )}
                              </Stack>
                              {res.notes && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                  📝 {res.notes}
                                </Typography>
                              )}
                            </Box>

                            {/* Fecha, Hora, Personas */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
                              <Chip label={`📆 ${res.date}`} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                              <Chip label={`🕒 ${res.time}`} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                              <Chip label={`👥 ${res.guestCount} pers.`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                            </Stack>

                            {/* Paquete & Precio */}
                            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                🎁 {res.packageTitle || 'Mesa Estándar'}
                              </Typography>
                              <Typography variant="subtitle2" color="primary" fontWeight={900}>
                                {res.packagePrice ? `$${formatPrice(res.packagePrice)}` : 'Gratuito'}
                              </Typography>
                            </Box>

                            {/* Acciones */}
                            <Stack direction="row" spacing={1} justifyContent="flex-end" pt={1} borderTop="1px solid #f1f5f9">
                              {res.state === 'pendiente' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleStatusChange(res.id, 'confirmada')}
                                  sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
                                >
                                  Confirmar
                                </Button>
                              )}
                              {res.state === 'confirmada' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleStatusChange(res.id, 'completada')}
                                  sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
                                >
                                  Completar
                                </Button>
                              )}
                              {res.state !== 'cancelada' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => handleStatusChange(res.id, 'cancelada')}
                                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                                >
                                  Cancelar
                                </Button>
                              )}
                              <IconButton color="error" size="small" onClick={() => handleDeleteRes(res.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </Stack>
              ) : (
                /* Tabla Desktop */
                <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', elevation: 0 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>CÓDIGO</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>CLIENTE</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>FECHA Y HORA</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>PERSONAS</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>PAQUETE / PRECIO</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>ESTADO</TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="right">ACCIONES</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredReservations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No se encontraron reservas con los filtros aplicados.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredReservations.map((res) => {
                          const cleanPhone = res.customerPhone ? res.customerPhone.replace(/\D/g, '') : '';
                          const waUrl = cleanPhone ? `https://wa.me/${cleanPhone}` : null;

                          return (
                            <TableRow key={res.id} hover>
                              <TableCell sx={{ fontWeight: 900, color: '#001a64' }}>
                                #{res.reservationCode}
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" fontWeight={800}>{res.customerName}</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Typography variant="caption" color="text.secondary">{res.customerPhone}</Typography>
                                  {waUrl && (
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={waUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ color: '#25D366', p: 0.2 }}
                                      title="Abrir WhatsApp del cliente"
                                    >
                                      <WhatsAppIcon fontSize="inherit" />
                                    </IconButton>
                                  )}
                                </Stack>
                                {res.notes && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                                    📝 {res.notes}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={700}>{res.date}</Typography>
                                <Typography variant="caption" color="text.secondary">🕒 {res.time}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={`👥 ${res.guestCount} pers.`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={700}>{res.packageTitle || 'Mesa Estándar'}</Typography>
                                {res.packagePrice ? (
                                  <Typography variant="caption" color="primary" fontWeight={800}>
                                    ${formatPrice(res.packagePrice)}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">Gratuito</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {renderStatusChip(res.state)}
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="Ver Detalle">
                                    <IconButton size="small" onClick={() => setSelectedResForDetail(res)}>
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {res.state === 'pendiente' && (
                                    <Tooltip title="Confirmar Reserva">
                                      <IconButton color="success" size="small" onClick={() => handleStatusChange(res.id, 'confirmada')}>
                                        <CheckCircleIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {res.state === 'confirmada' && (
                                    <Tooltip title="Marcar como Completada">
                                      <IconButton color="primary" size="small" onClick={() => handleStatusChange(res.id, 'completada')}>
                                        <CheckCircleIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {res.state !== 'cancelada' && (
                                    <Tooltip title="Cancelar Reserva">
                                      <IconButton color="warning" size="small" onClick={() => handleStatusChange(res.id, 'cancelada')}>
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Eliminar Documento">
                                    <IconButton color="error" size="small" onClick={() => handleDeleteRes(res.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── PESTAÑA 2: PAQUETES DE RESERVA ── */}
      {tab === 1 && (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={800} color="#0f172a">
              Paquetes Configurables para Clientes
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenNewPkg}
              sx={{ bgcolor: '#001a64', borderRadius: 3, fontWeight: 800, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
            >
              Nuevo Paquete
            </Button>
          </Stack>

          <Grid container spacing={3}>
            {packages.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0', elevation: 0 }}>
                  <Typography variant="body1" color="text.secondary">
                    No tienes ningún paquete de reserva creado aún. Haz clic en "Nuevo Paquete" para añadir opciones como "Cumpleaños", "Aniversario" o "Fiesta Privada".
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              packages.map((pkg) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pkg.id}>
                  <Card sx={{ borderRadius: 3, border: pkg.active !== false ? '1px solid #e2e8f0' : '1px dashed #cbd5e1', opacity: pkg.active !== false ? 1 : 0.65, boxShadow: 'none', position: 'relative' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="h6" fontWeight={800} color="#0f172a">
                          {pkg.title}
                        </Typography>
                        <Typography variant="h6" fontWeight={900} color="#001a64">
                          ${formatPrice(pkg.price)}
                        </Typography>
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {pkg.description || 'Sin descripción'}
                      </Typography>

                      <Chip label={`👥 Hasta ${pkg.maxGuests} personas`} size="small" sx={{ mb: 2, fontWeight: 700, bgcolor: '#f1f5f9' }} />

                      {pkg.features && pkg.features.length > 0 && (
                        <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 2, border: '1px solid #f1f5f9' }}>
                          <Typography variant="caption" fontWeight={800} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            INCLUYE:
                          </Typography>
                          {pkg.features.map((feat, idx) => (
                            <Typography key={idx} variant="caption" display="block" color="#334155">
                              • {feat}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1, borderTop: '1px solid #f1f5f9' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={pkg.active !== false}
                              onChange={() => handleTogglePkgActive(pkg)}
                              size="small"
                            />
                          }
                          label={<Typography variant="caption" fontWeight={700}>{pkg.active !== false ? 'Activo' : 'Inactivo'}</Typography>}
                        />

                        <Stack direction="row" spacing={1}>
                          <IconButton size="small" onClick={() => handleEditPkg(pkg)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeletePkg(pkg.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      )}

      {/* ── MODAL: RESERVAS DEL DÍA SELECCIONADO EN CALENDARIO ── */}
      <Dialog
        open={openDayModal}
        onClose={() => setOpenDayModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 900, color: '#001a64' }}>
          <span>Reservas para el {dayModalDate}</span>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpenDayModal(false);
              handleCreateForDate(dayModalDate);
            }}
            sx={{ bgcolor: '#001a64', textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
          >
            Nueva Reserva
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {(!reservationsByDate[dayModalDate] || reservationsByDate[dayModalDate].length === 0) ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No hay reservas programadas para este día.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ py: 1 }}>
              {reservationsByDate[dayModalDate].map((res) => {
                const cleanPhone = res.customerPhone ? res.customerPhone.replace(/\D/g, '') : '';
                const waUrl = cleanPhone ? `https://wa.me/${cleanPhone}` : null;

                return (
                  <Paper key={res.id} variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                          🕒 {res.time} - {res.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          👥 {res.guestCount} personas • 🎁 {res.packageTitle || 'Mesa Estándar'}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                          <Typography variant="caption" color="text.secondary">{res.customerPhone}</Typography>
                          {waUrl && (
                            <Button
                              size="small"
                              component="a"
                              href={waUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
                              sx={{ color: '#25D366', textTransform: 'none', py: 0, px: 0.5, fontSize: '0.75rem', fontWeight: 700 }}
                            >
                              WhatsApp
                            </Button>
                          )}
                        </Stack>
                      </Box>

                      <Box textAlign="right">
                        {renderStatusChip(res.state)}
                        <Typography variant="caption" fontWeight={800} color="#001a64" display="block" sx={{ mt: 0.5 }}>
                          #{res.reservationCode}
                        </Typography>
                      </Box>
                    </Stack>

                    {res.notes && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mb: 1, p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                        📝 {res.notes}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {res.state === 'pendiente' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleStatusChange(res.id, 'confirmada')}
                          sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 1.5 }}
                        >
                          Confirmar
                        </Button>
                      )}
                      {res.state === 'confirmada' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleStatusChange(res.id, 'completada')}
                          sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 1.5 }}
                        >
                          Completar
                        </Button>
                      )}
                      {res.state !== 'cancelada' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleStatusChange(res.id, 'cancelada')}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                        >
                          Cancelar
                        </Button>
                      )}
                      <IconButton color="error" size="small" onClick={() => handleDeleteRes(res.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDayModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL: VER DETALLE INDIVIDUAL DE RESERVA ── */}
      <Dialog
        open={Boolean(selectedResForDetail)}
        onClose={() => setSelectedResForDetail(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedResForDetail && (
          <>
            <DialogTitle sx={{ fontWeight: 900, color: '#001a64', pb: 1 }}>
              Reserva #{selectedResForDetail.reservationCode}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>ESTADO</Typography>
                  <Box mt={0.5}>{renderStatusChip(selectedResForDetail.state)}</Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>CLIENTE</Typography>
                  <Typography variant="subtitle1" fontWeight={800}>{selectedResForDetail.customerName}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedResForDetail.customerPhone}</Typography>
                  {selectedResForDetail.customerEmail && (
                    <Typography variant="caption" color="text.secondary" display="block">{selectedResForDetail.customerEmail}</Typography>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>FECHA Y HORA</Typography>
                    <Typography variant="body2" fontWeight={800}>📆 {selectedResForDetail.date}</Typography>
                    <Typography variant="body2" fontWeight={800}>🕒 {selectedResForDetail.time}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>INVITADOS</Typography>
                    <Typography variant="body2" fontWeight={800}>👥 {selectedResForDetail.guestCount} personas</Typography>
                  </Grid>
                </Grid>
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>PAQUETE SELECCIONADO</Typography>
                  <Typography variant="subtitle2" fontWeight={800}>{selectedResForDetail.packageTitle || 'Mesa Estándar'}</Typography>
                  <Typography variant="caption" color="primary" fontWeight={800}>
                    {selectedResForDetail.packagePrice ? `$${formatPrice(selectedResForDetail.packagePrice)}` : 'Gratuito'}
                  </Typography>
                </Box>
                {selectedResForDetail.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>NOTAS / PETICIONES</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', bgcolor: '#fffbe6', p: 1, borderRadius: 1.5, border: '1px solid #ffe58f' }}>
                      📝 {selectedResForDetail.notes}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
              <Button color="error" size="small" onClick={() => handleDeleteRes(selectedResForDetail.id)}>
                Eliminar
              </Button>
              <Stack direction="row" spacing={1}>
                {selectedResForDetail.state === 'pendiente' && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleStatusChange(selectedResForDetail.id, 'confirmada')}
                    sx={{ fontWeight: 800, textTransform: 'none' }}
                  >
                    Confirmar
                  </Button>
                )}
                {selectedResForDetail.state === 'confirmada' && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleStatusChange(selectedResForDetail.id, 'completada')}
                    sx={{ fontWeight: 800, textTransform: 'none' }}
                  >
                    Completar
                  </Button>
                )}
                <Button onClick={() => setSelectedResForDetail(null)} size="small">
                  Cerrar
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── MODAL RESERVA MANUAL ── */}
      <Dialog
        open={openResModal}
        onClose={() => setOpenResModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            p: { xs: 0.5, sm: 1 },
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: '95vh', sm: '90vh' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#001a64' }}>Crear Reserva Manual</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Nombre del Cliente *"
                  fullWidth
                  value={resForm.customerName}
                  onChange={(e) => setResForm({ ...resForm, customerName: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Teléfono / WhatsApp *"
                  fullWidth
                  value={resForm.customerPhone}
                  onChange={(e) => setResForm({ ...resForm, customerPhone: e.target.value })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Fecha *"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={resForm.date}
                  onChange={(e) => setResForm({ ...resForm, date: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Hora *"
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={resForm.time}
                  onChange={(e) => setResForm({ ...resForm, time: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Personas *"
                  type="number"
                  fullWidth
                  value={resForm.guestCount}
                  onChange={(e) => setResForm({ ...resForm, guestCount: Number(e.target.value) })}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Paquete de Reserva</InputLabel>
              <Select
                value={resForm.packageTitle}
                label="Paquete de Reserva"
                onChange={(e) => {
                  const title = e.target.value;
                  const found = packages.find((p) => p.title === title);
                  setResForm({
                    ...resForm,
                    packageTitle: title,
                    packagePrice: found?.price || 0,
                  });
                }}
              >
                <MenuItem value="Reserva Estándar">Mesa Estándar (Sin Paquete)</MenuItem>
                {packages.map((p) => (
                  <MenuItem key={p.id} value={p.title}>
                    {p.title} (${formatPrice(p.price)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notas o Peticiones Especiales"
              multiline
              rows={2}
              fullWidth
              value={resForm.notes}
              onChange={(e) => setResForm({ ...resForm, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenResModal(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button onClick={handleCreateReservation} variant="contained" sx={{ bgcolor: '#001a64', borderRadius: 2, textTransform: 'none' }}>
            Guardar Reserva
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL PAQUETE (CREAR / EDITAR) ── */}
      <Dialog
        open={openPkgModal}
        onClose={() => setOpenPkgModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            p: { xs: 0.5, sm: 1 },
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: '95vh', sm: '90vh' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#001a64' }}>
          {editingPkgId ? 'Editar Paquete' : 'Nuevo Paquete de Reserva'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Título del Paquete *"
              placeholder="Ej: Paquete Cumpleañero"
              fullWidth
              value={pkgForm.title}
              onChange={(e) => setPkgForm({ ...pkgForm, title: e.target.value })}
            />

            <TextField
              label="Descripción Breve"
              placeholder="Ej: Ideal para celebrar cumpleaños con amigos..."
              fullWidth
              multiline
              rows={2}
              value={pkgForm.description}
              onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })}
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Precio ($ COP) *"
                  type="text"
                  fullWidth
                  value={pkgForm.price ? pkgForm.price.toLocaleString('es-CO') : ''}
                  onChange={(e) => {
                    const rawStr = e.target.value.replace(/\D/g, '');
                    const val = rawStr ? Number(rawStr) : 0;
                    setPkgForm({ ...pkgForm, price: val });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography fontWeight={700} color="text.secondary">$</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Capacidad Máxima (Personas) *"
                  type="number"
                  fullWidth
                  value={pkgForm.maxGuests}
                  onChange={(e) => setPkgForm({ ...pkgForm, maxGuests: Number(e.target.value) })}
                />
              </Grid>
            </Grid>

            <TextField
              label="Lo que incluye (Un ítem por línea)"
              placeholder={`1 Pizza Familiar\n1 Gaseosa 1.5L\nDecoración con globos`}
              fullWidth
              multiline
              rows={4}
              value={pkgForm.featuresText}
              onChange={(e) => setPkgForm({ ...pkgForm, featuresText: e.target.value })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={pkgForm.active}
                  onChange={(e) => setPkgForm({ ...pkgForm, active: e.target.checked })}
                />
              }
              label="Activo para selección de clientes"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPkgModal(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button onClick={handleSavePkg} variant="contained" sx={{ bgcolor: '#001a64', borderRadius: 2, textTransform: 'none' }}>
            Guardar Paquete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
